<template>
  <div class="offer-card">
    <img
      v-if="upgradeRequiredReasons.length"
      src="@/assets/icons/handdrawn-arrow.svg"
      alt="handdrawn arrow"
      class="offer-arrow"
    />

    <img
      src="@/assets/icons/paper-plane.svg"
      class="offer-icon"
      alt="Paper Plane Icon"
    />

    <h1 class="offer-title">
      {{ freeTier ? "Let us send for you..." : "Upgrade required to send..." }}
    </h1>

    <p class="offer-subtitle">
      Get all these features and more when you send with Promo&hellip;
    </p>

    <ul class="offer-features">
      <UpgradeOfferFeature v-if="features && features.maxEmails">
        Send up to
        <span class="font-lato text-brand sm:text-base md:text-lg">
          {{ features.maxEmails }}
        </span>
        email{{ features.maxEmails === 1 ? "" : "s" }} per month
      </UpgradeOfferFeature>

      <UpgradeOfferFeature
        v-for="(feature, i) in featureList"
        :key="i"
        :title="feature"
      />

      <UpgradeOfferFeature v-if="features && features.removePromoBranding">
        Remove our branding
      </UpgradeOfferFeature>
    </ul>

    <!-- eslint-disable -->
    <!-- Disable eslint here otherwise it adds whitespace to the span  -->
    <p class="offer-price" v-if="plan.price">
      only<span class="offer-price-amount">${{ plan.price }}</span
      >/month
    </p>
    <!-- eslint-enable -->

    <div v-if="plan.price" class="flex flex-col justify-center">
      <p v-if="plan.price" class="offer-meta text-xs">
        billed
        <UiInlineSelect
          class="border-b border-dashed border-support-lightest font-semibold"
          :arrow-size="15"
          @change="onSubscriptionChange"
        >
          <option value="Annual" :selected="subscription === 'Annual'"
            >annually</option
          >
          <option value="Monthly" :selected="subscription === 'Monthly'"
            >monthly</option
          >
        </UiInlineSelect>
      </p>

      <button
        v-if="subscription === 'Monthly'"
        class="offer-savings-tag text-success text-xxs underline mt-2 outline-none animate__animated animate__flash"
        @click="subscription = 'Annual'"
      >
        Select yearly billing and save!
      </button>

      <p
        v-if="subscription === 'Annual'"
        class="offer-savings-tag mt-2 animate__animated animate__tada"
      >
        You saved {{ plan.discountPercentage }}% 🥳
      </p>
      <!-- .offer-savings-tag -->

      <p class="offer-meta text-support-lighter italic mt-1">
        Cancel at anytime :)
      </p>
    </div>
    <!-- .flex items-center -->

    <p v-if="upgradeUrl" class="offer-info mt-4">
      We'll redirect you to Shopify to approve the charge and then return you
      here before we send your newsletter.
    </p>

    <Button
      v-if="upgradeUrl"
      class="secondary mb-10 mx-auto"
      :loading="upgrading"
      @click="onUpgradeClick"
    >
      {{ upgradeButtonLabel }}
    </Button>
  </div>
</template>

<script>
import UiInlineSelect from "@/components/UI/UiInlineSelect";
import UpgradeOfferFeature from "@/components/Upgrade/UpgradeOfferFeature";

export default {
  name: "UpgradeOffer",
  components: { UiInlineSelect, UpgradeOfferFeature },
  props: {
    accountId: {
      type: String,
      default: "",
    },
    freeTier: {
      type: Boolean,
      default: true,
    },
    package: {
      type: Object,
      default: null,
    },
    upgradeRequiredReasons: {
      type: Array,
      default: () => [],
    },
  },
  data() {
    return {
      featureList: [
        "Bulk sending to verified customers",
        "Auto-import your Shopify customers",
        "Unsubscribes and GDPR compliance",
        "Mailing list and address validation",
        "Integrated analytics and tracking",
        "Ability to Upload Custom Images",
        "Email SFP and security included",
        "Friendly expert support 😊",
      ],
      subscription: "Monthly",
      upgrading: false,
    };
  },
  computed: {
    features() {
      return this.package?.features;
    },
    plan() {
      return this.package?.plans
        ?.filter((plan) => plan.subscription === this.subscription)
        .reduce((acc) => acc);
    },
    upgradeButtonLabel() {
      return this.upgrading ? "Redirecting to Shopify" : "Get Features";
    },
    upgradeUrl() {
      return this.plan?.upgradeUrl;
    },
  },
  methods: {
    onSubscriptionChange(subscription) {
      this.subscription = subscription;
    },
    onUpgradeClick() {
      this.upgrading = true;

      this.$gtm.trackEvent({
        event: "promo-upgrade-started",
        accountId: this.accountId,
      });

      this.$nextTick(() => {
        // After the next DOM update cycle redirect the user to the upgrade page
        // to ensure the gtm track event above is fired
        window.document.location.href = this.upgradeUrl;
      });
    },
  },
};
</script>

<style scoped>
.offer-card {
  @apply p-6 text-center mb-12 mx-6 max-w-sm relative;

  background: linear-gradient(141.39deg, #f9fafb 0.93%, #f8f9ff 100.36%);
  border: 1px solid #dae1e7;
  box-shadow: 0px 15px 34px rgba(0, 0, 0, 0.11);
  border-radius: 21px;

  @screen sm {
    @apply mx-auto;
    transform: rotate(-1deg);
  }
}

.offer-icon {
  @apply w-auto mb-4;
  height: 80px;
  transform: rotate(14deg);

  @screen lg {
    @apply my-4 mb-8;
    height: 100px;
  }
}

.offer-title {
  @apply font-lato text-support text-xl mb-4;

  @screen md {
    @apply mb-6 text-2xl;
  }
}

.offer-subtitle {
  @apply text-support-light text-xs mx-auto italic mb-6;
  max-width: 260px;
}

.offer-features {
  @apply list-reset m-0 mb-4;
}

.cross-icon {
  @apply m-0;

  width: 10px;

  @screen sm {
    @apply mr-1;
  }
}

.offer-price {
  @apply text-grey-dark text-sm font-sans font-normal mb-0;
}

.offer-price-amount {
  @apply font-lato text-grey-darkest text-lg;

  @screen md {
    @apply text-2xl;
  }
}

.offer-info {
  @apply text-xs text-grey-darker max-w-xs mx-auto;
}

.offer-arrow {
  @apply block absolute opacity-75;
  right: -34px;
  top: -55px;
  transform: rotate(-13deg);
  width: 55px;

  @screen lg {
    @apply hidden;
  }
}

.offer-meta {
  @apply text-support-lighter mb-0;
  font-size: 11px;
}

.offer-savings-tag {
  @apply leading-tight font-lato text-success mb-0 text-xs;
}
</style>
